/**
 * Adaptateur de stockage (dossier d'architecture, ch. 2.2 et 7.6).
 *
 * Le parcours ne parle jamais directement à IndexedDB : au passage en hybride
 * (Capacitor), seule cette implémentation change, la graine passant alors dans
 * le trousseau du système. Rien d'autre n'est à réécrire.
 */

export interface Storage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  /** Demande au navigateur de ne pas effacer ces données. */
  requestPersistence(): Promise<boolean>;
  /** Efface tout : « supprimer mon compte » est immédiat et local. */
  clear(): Promise<void>;
}

/**
 * Le nom de la base locale. **Il ne suit pas le renommage en Hoa**, pour la
 * même raison : un téléphone qui a déjà un compte l'a rangé sous ce nom-là.
 * Changer le nom ouvrirait une base vide à côté, et l'utilisateur trouverait
 * son compte disparu sans que rien n'ait été effacé.
 */
const DB = "gomgom";
const STORE = "compte";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => {
      // Une demande d'effacement de la base ne doit pas rester bloquée par cette connexion.
      req.result.onversionchange = () => req.result.close();
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const indexedDbStorage: Storage = {
  async get(key) {
    try {
      const v = await tx<unknown>("readonly", (s) => s.get(key) as IDBRequest<unknown>);
      return typeof v === "string" ? v : null;
    } catch {
      return null;
    }
  },
  async set(key, value) {
    await tx("readwrite", (s) => s.put(value, key) as IDBRequest<unknown>);
  },
  async remove(key) {
    await tx("readwrite", (s) => s.delete(key) as IDBRequest<unknown>);
  },
  async requestPersistence() {
    try {
      if (navigator.storage?.persist) return await navigator.storage.persist();
    } catch {
      /* certains navigateurs refusent, ce n'est pas bloquant */
    }
    return false;
  },
  async clear() {
    await tx("readwrite", (s) => s.clear() as IDBRequest<unknown>);
  },
};

/** Repli en mémoire, utilisé si IndexedDB est indisponible (navigation privée). */
export function memoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    async get(k) {
      return map.get(k) ?? null;
    },
    async set(k, v) {
      map.set(k, v);
    },
    async remove(k) {
      map.delete(k);
    },
    async requestPersistence() {
      return false;
    },
    async clear() {
      map.clear();
    },
  };
}

export const storage: Storage = typeof indexedDB === "undefined" ? memoryStorage() : indexedDbStorage;
